import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/motion";
import { ApplicationForm } from "@/components/forms/ApplicationForm";
import { ROLES } from "@/content/roles";
import NotFound from "@/pages/NotFound";

const List = ({ title, items }) => (
  <Reveal className="border-t border-white/10 pt-8">
    <h2 className="eyebrow text-fg-3">{title}</h2>
    <ul className="mt-6 space-y-4">
      {items.map((it) => (
        <li key={it} className="flex gap-4 text-lg leading-relaxed text-fg">
          <span className="mt-3 h-1.5 w-1.5 shrink-0 rounded-full bg-signal" />
          {it}
        </li>
      ))}
    </ul>
  </Reveal>
);

export default function Role() {
  const { slug } = useParams();
  const role = ROLES.find((r) => r.slug === slug);
  if (!role) return <NotFound />;
  const others = ROLES.filter((r) => r.slug !== slug);

  return (
    <div data-testid={`role-page-${role.slug}`}>
      <PageHero
        testId="role-hero"
        eyebrow={`Careers · ${role.discipline}`}
        lines={[role.title]}
        text={role.line}
        titleClass="mt-6 text-5xl font-[560] leading-[0.95] tracking-[-0.045em] text-fg sm:text-6xl lg:text-[5.4rem]"
      >
        <Reveal delay={0.4} className="mt-8">
          <Link to="/careers" className="inline-flex items-center gap-2 text-[15px] text-fg-2 hover:text-fg" data-testid="role-back-link"><ArrowLeft className="h-4 w-4" /> All roles</Link>
        </Reveal>
      </PageHero>

      <section className="shell grid gap-16 pb-24 sm:pb-32 lg:grid-cols-12">
        <div className="space-y-14 lg:col-span-6">
          <Reveal>
            <h2 className="eyebrow text-fg-3">The problem</h2>
            <p className="mt-6 text-2xl font-[480] leading-snug tracking-[-0.015em] text-fg sm:text-[1.7rem]">{role.problem}</p>
          </Reveal>
          <List title="What you might work on" items={role.work} />
          <List title="You might be someone who" items={role.you} />
          <Reveal>
            <p className="rounded-2xl border border-white/[0.08] p-5 text-[14px] leading-relaxed text-fg-2" data-testid="role-jd-note">
              The full job description for this role will be published soon. If this is the problem you want to work on, apply now.
            </p>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <div className="rounded-3xl border border-white/[0.08] bg-ink-2 p-6 sm:p-10 lg:sticky lg:top-24">
            <ApplicationForm role={role.slug} roleTitle={role.title} />
          </div>
        </div>
      </section>

      <section className="border-t border-white/[0.06] py-16">
        <div className="shell">
          <p className="eyebrow text-fg-3">Other roles</p>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {others.map((r) => (
              <Link key={r.slug} to={`/careers/${r.slug}`} className="group flex items-center justify-between rounded-2xl border border-white/[0.08] p-5 transition-colors hover:border-signal/50" data-testid={`other-role-${r.slug}`}>
                <span className="text-lg font-[520] text-fg">{r.title}</span>
                <ArrowUpRight className="h-4 w-4 text-fg-2 transition-transform group-hover:rotate-45 group-hover:text-signal" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
